<template>
    <v-card class="mt-2" v-if="hasRooms">
        <v-toolbar>
            <v-toolbar-title
                :class="
                    $vuetify.display.mdAndUp ? 'text-h6' : 'text-subtitle-1'
                "
                >Detalii produse</v-toolbar-title
            >
            <v-spacer></v-spacer>

           

            <!-- Desktop + Mobile  -->
            <template v-if="$vuetify.display.mdAndUp">
                <v-select
                    v-model="customizationValue"
                    :items="customizationOptionsFiltered"
                    item-value="value"
                    item-title="label"
                    label="Personalizare"
                    hide-details
                    density="compact"
                    variant="outlined"
                    class="mr-6"
                    style="max-width: 240px; min-width: 160px"
                />
            </template>

            <template v-else>
                <v-btn icon variant="text" title="Schimbă personalizarea">
                    <v-icon>mdi-tune-variant</v-icon>
                    <v-menu activator="parent">
                        <v-list>
                            <v-list-item
                                v-for="item in customizationOptionsFiltered"
                                :key="item.value"
                                @click="customizationValue = item.value"
                                :active="customizationValue === item.value"
                            >
                                <v-list-item-title>{{
                                    item.label
                                }}</v-list-item-title>
                            </v-list-item>
                        </v-list>
                    </v-menu>
                </v-btn>
            </template>
        </v-toolbar>

        <v-row no-gutters align="stretch" :key="`custom-${effectiveCustomization}`">
            <v-col
                v-for="u in customUnits"
                :key="`c${customizationType}-u${u.id}`"
                :md="unitSpanMd"
                :lg="unitSpanLg"
                class="d-flex"
            >
                <ProductUnitDetails
                    :title="u.label"
                    :location="uiLocation"
                    :effective-compartmentation="effectiveCompartmentation"
                    :effective-customization="effectiveCustomization"
                    :initialData="unitInitialData(u.id)"
                    :insulation-types="insulationTypes"
                    :tacker-foil-types="tackerFoilTypes"
                    :additional-insulation-types="additionalInsulationTypes"
                    :show-apply-to-other-units="showApplyToOtherUnitsButton"
                    :apply-to-other-units-label="applyToOtherUnitsLabel"
                    :apply-to-other-units-tooltip="applyToOtherUnitsTooltip"
                    :card-floor-color="cardFloorColorForUnit(u)"
                    @update="(payload) => onUnitUpdate(payload, u.id)"
                    @apply-to-other-units="applyUnitDataToOtherUnits(u.id)"
                />
            </v-col>
        </v-row>
    </v-card>
</template>

<script>
import ProductUnitDetails from "@/components/offer/ProductUnitDetails.vue";
import { FLOOR_MAP } from "@/IPA/Floors";
import { floorDisplayLabel, floorSortKey, normalizeFloorValue } from "@/utils/floorUtils";
import { APARTMENT_MAP } from "@/IPA/Apartments";
import { getApartamentLabel, getUnitId } from "@/utils/apartmentUtils";
import { CUSTOMIZATION_ENUM, CUSTOMIZATION_OPTIONS } from "@/IPA/Customization";
import {
    DEFAULT_PIPE_DIAMETER,
    resolveAvailablePipe,
} from "@/IPA/ProductChoices.js";

/** Colours for card border when customization is 2 (apartments), keyed by floor. */
const FLOOR_CARD_COLORS = [
    "#B71C1C", // Subsol
    "#E65100", // Demisol
    "#F9A825", // Parter
    "#263238", // Mansarda
    "#C0CA33", // Nivel 1
    "#7CB342", // Nivel 2
    "#2E7D32", // Nivel 3
    "#00897B", // Nivel 4
    "#00ACC1", // Nivel 5
    "#039BE5", // Nivel 6
    "#1E88E5", // Nivel 7
    "#3949AB", // Nivel 8
    "#5E35B1", // Nivel 9
    "#8E24AA", // Nivel 10
    "#AB47BC", // Nivel 11
    "#D81B60", // Nivel 12
    "#E53935", // Nivel 13
    "#6D4C41", // Nivel 14
    "#546E7A", // Nivel 15
    "#004D40", // Nivel 16
];

export default {
    name: 'ProductDetailsForm',
    components: { ProductUnitDetails },

    props: {
        // Parent owns the state; we mirror it via v-model
        modelValue: {
            type: Object, // shape: { [compType]: { [unitId]: {...payload} } }
            default: () => ({})
        },
      

        location: {
            type: Object,
            required: true // { rooms: [...], compartmentation: 0|1|2 }
        },
        /** Effective compartmentation (from offer with fallback); use instead of location.compartmentation. */
        effectiveCompartmentation: { type: Number, default: 0 },
        /** Effective customization (from offer with fallback); use instead of location.customization. */
        effectiveCustomization: { type: Number, default: 0 },
        insulationTypes: {
            type: Array
        },
        additionalInsulationTypes: {
            type: Array
        },
        tackerFoilTypes: {
            type: Array
        },
        showTotalProducts: {
            type: Boolean,
            default: false
        },
        showApartments: { type: Boolean, default: false },
        offerType: { type: Object, default: () => ({}) },
        /** When true (e.g. new offer), initial product details use defaults instead of location/room values. */
        useDefaultProductDetails: { type: Boolean, default: false }
    },
    emits: [
        'update:modelValue',
        'update:showTotalProducts',
        'update-customization',
        'changed'
    ],

    data() {
        return {
            FLOOR_MAP,
            APARTMENT_MAP,
            customizationEnum: CUSTOMIZATION_ENUM,
            customizationOptions: CUSTOMIZATION_OPTIONS
        };
    },
    mounted() {
        this.localRooms = this.location?.rooms;
    },

    computed: {
        unitSpanLg() {
            if (this.customizationType === 0) return 12; // whole location
            const n = this.customUnits.length;
            if (n <= 1) return 12;
            if (n === 2) return 6;
            return 4;
        },
        unitSpanMd() {
            if (this.customizationType === 0) return 12; // whole location
            const n = this.customUnits.length;
            if (n <= 1) return 12;
            if (n === 2) return 6;
            return 6;
        },
        compartmentationOptionsFiltered() {
            return this.showApartments
                ? this.compartmentationOptions
                : this.compartmentationOptions.filter((o) => o.value !== 2);
        },
        customizationOptionsFiltered() {
            if (this.showApartments) {
                return this.customizationOptions;
            }
            const currentValue = Number(
                this.effectiveCustomization ?? this.location?.customization ?? 0
            );
            return this.customizationOptions.filter(
                (o) => o.value !== 2 || o.value === currentValue
            );
        },
        uiLocation() {
            const { customization, rooms } = this.location || {};
            return { customization, rooms };
        },

        customizationValue: {
            get() {
                const val = this.effectiveCustomization ?? this.location?.customization;
                return val !== undefined && val !== null
                    ? Number(val)
                    : undefined;
            },
            set(value) {
                this.$emit('update-customization', value);
            }
        },
        customizationType() {
            return this.effectiveCustomization ?? this.location?.customization ?? 0;
        },
        hasRooms() {
            return !!(
                this.location &&
                this.location.rooms &&
                this.location.rooms.length
            );
        },
        compType() {
            return Number(this.effectiveCompartmentation ?? this.location?.compartmentation ?? 0); // 0 = whole, 1 = floors, 2 = apartments
        },
        floors() {
            const s = new Set();
            for (const r of this.location.rooms || []) {
                if (r.floor !== undefined && r.floor !== null)
                    s.add(normalizeFloorValue(r.floor));
            }
            return [...s].sort((a, b) => {
                const [ta, va] = floorSortKey(a);
                const [tb, vb] = floorSortKey(b);
                if (ta !== tb) return ta - tb;
                if (typeof va === "number" && typeof vb === "number") return va - vb;
                return String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: "base" });
            });
        },
        apartments() {
            const s = new Set();

            for (const r of this.location.rooms || []) {
                const a = r.apartament ?? r.apartment ?? '0';
                s.add(String(a));
            }

            if (s.size === 0) s.add('0');

            return [...s].sort((a, b) => a - b);
        },

        customUnits() {
            switch (this.customizationType) {
                case 0:
                    return [{ id: 0, label: 'Întreaga locație' }];
                case 1:
                    return this.floors.map((f) => ({
                        id: f,
                        label: floorDisplayLabel(f)
                    }));
                case 2:
                    return this.apartments
                        .map((a) => {
                            const floorId = this.getFloorForApartment(a);
                            const apartmentLabel = getApartamentLabel(a);
                            const floorLabel =
                                floorId == null
                                    ? null
                                    : (this.FLOOR_MAP[floorId] ?? `Etaj ${floorId}`);
                            return {
                                id: a,
                                label: floorLabel
                                    ? `${apartmentLabel} (${floorLabel})`
                                    : apartmentLabel,
                                floorId
                            };
                        })
                        .sort((a, b) => {
                            const [ta, va] = floorSortKey(a.floorId);
                            const [tb, vb] = floorSortKey(b.floorId);

                            // Keep "Mansarda" (floor value 3) always last in card ordering.
                            const aIsMansarda = normalizeFloorValue(a.floorId) === "3";
                            const bIsMansarda = normalizeFloorValue(b.floorId) === "3";
                            if (aIsMansarda !== bIsMansarda) return aIsMansarda ? 1 : -1;

                            if (ta !== tb) return ta - tb;
                            if (typeof va === "number" && typeof vb === "number" && va !== vb) return va - vb;
                            if (String(va) !== String(vb)) return String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: "base" });

                            // Stable, human-friendly order inside the same floor.
                            return String(a.id).localeCompare(String(b.id), undefined, {
                                numeric: true,
                                sensitivity: "base"
                            });
                        });
                default:
                    return [{ id: 0, label: 'Nedefinit' }];
            }
        },
        showApplyToOtherUnitsButton() {
            return (
                (this.customizationType === this.customizationEnum.FLOORS ||
                    this.customizationType === this.customizationEnum.APARTMENTS) &&
                this.customUnits.length > 1
            );
        },
        applyToOtherUnitsLabel() {
            if (this.customizationType === this.customizationEnum.FLOORS) {
                return 'Aplică pe celelalte etaje';
            }
            if (this.customizationType === this.customizationEnum.APARTMENTS) {
                return 'Aplică pe celelalte apartamente';
            }
            return 'Aplică pe celelalte nivele';
        },
        applyToOtherUnitsTooltip() {
            if (this.customizationType === this.customizationEnum.FLOORS) {
                return 'Aplică pe toate etajele';
            }
            if (this.customizationType === this.customizationEnum.APARTMENTS) {
                return 'Aplică pe toate apartamentele';
            }
            return 'Aplică pe toate nivelele';
        }
    },

    methods: {
        /**
         * Retrieves initial configuration data for a specific unit.
         * Returns existing model data if available, otherwise builds default data.
         *
         * @param {string|number} unitId - The unique identifier of the unit.
         * @returns {Object} The configuration data for the specified unit.
         */
        unitInitialData(unitId) {
            const normalizedId = this.normalizeUnitId(
                unitId,
                this.customizationType
            );
            const existing =
                this.modelValue?.[this.customizationType]?.[normalizedId];
            if (existing) return existing;
            return this.getInitialData(unitId);
        },
        /**
         * Emits an event to toggle the visibility of total products.
         *
         * @param {boolean} value - True to show total products, false to hide.
         */
        onToggleTotalProducts(value) {
            this.$emit('update:showTotalProducts', value);
            this.$emit('changed');
        },
        /**
         * Updates data for a specific unit and emits the new model value.
         * Keeps only the data related to the current customization type.
         *
         * @param {Object} payload - Data payload to update the unit with.
         * @param {number|string} unitId - The unique identifier for the unit.
         */
        onUnitUpdate(payload, unitId) {
            const next = { ...(this.modelValue || {}) };

            if (!next[this.customizationType]) {
                next[this.customizationType] = {};
            }

            const normalizedId = this.normalizeUnitId(
                unitId,
                this.customizationType
            );

            if (!next[this.customizationType]) {
                next[this.customizationType] = {};
            }

            next[this.customizationType][normalizedId] = {
                unit: normalizedId,
                rawUnit: unitId,
                ...payload
            };

            this.$emit('update:modelValue', next);
            this.$emit('changed');
        },
        /**
         * Copiază setările unui unit (etaj/apartament) pe toate celelalte unități.
         * Apelat când utilizatorul apasă "Aplică pe celelalte etaje/apartamente".
         */
        applyUnitDataToOtherUnits(sourceUnitId) {
            const prev = this.modelValue || {};
            const sourceNormId = this.normalizeUnitId(sourceUnitId, this.customizationType);
            const payload = prev[this.customizationType]?.[sourceNormId];
            if (!payload) return;
            // Build a new object for this customization type so Vue reactivity triggers on all children
            const newTypeData = {};
            for (const u of this.customUnits) {
                const normalizedId = this.normalizeUnitId(u.id, this.customizationType);
                newTypeData[normalizedId] = {
                    unit: normalizedId,
                    rawUnit: u.id,
                    ...payload
                };
            }
            const next = {
                ...prev,
                [this.customizationType]: newTypeData
            };
            this.$emit('update:modelValue', next);
            this.$emit('changed');
        },
        /**
         * Retrieves initial configuration data for a given unit.
         * The matching logic depends on the current customization type:
         * - 0: uses the first room
         * - 1: matches by floor
         * - 2: matches by apartment/apartament
         *
         * Returns merged defaults and matched room values.
         *
         * @param {number|string} unitId - Identifier of the unit to get data for.
         * @returns {Object} Initial configuration containing:
         *  - {number} pipeDiameter
         *  - {number} distributor
         *  - {number} metalBox
         *  - {number} temperatureRegulation
         *  - {number} automation
         *  - {number} reinforcementMesh
         */
        getInitialData(unitId) {
            const defaults = {
                pipeDiameter: DEFAULT_PIPE_DIAMETER,
                distributor: 3,
                metalBox: 2,
                temperatureRegulation: 1,
                automation: 1,
                reinforcementMesh: 1,
                automationSelection: [],
                insulationType: this.insulationTypes?.[0] ?? null,
                tackerFoil: null,
                additionalInsulations: []
            };

            if (this.useDefaultProductDetails) {
                return { ...defaults };
            }

            // For per-floor (1) or per-apartment (2), use defaults so that switching from
            // "Întreaga locație" (0) does not copy the whole-location choices onto each unit.
            // Existing saved data for that unit is provided via modelValue and unitInitialData.
            if (this.customizationType === 1 || this.customizationType === 2) {
                void unitId; // keep for API consistency with callers
                return { ...defaults };
            }

            const rooms = this.localRooms;
            const match = rooms?.[0] ?? null;

            return {
                pipeDiameter: resolveAvailablePipe(
                    match?.pipe_diameter ?? defaults.pipeDiameter
                ),
                distributor: match?.distributor ?? defaults.distributor,
                metalBox: match?.metal_box ?? defaults.metalBox,
                temperatureRegulation:
                    match?.temperature_regulation ??
                    defaults.temperatureRegulation,
                automation: match?.automation ?? defaults.automation,
                automationSelection:
                    match?.automationSelection ??
                    match?.automation_selection ??
                    defaults.automationSelection,
                reinforcementMesh:
                    match?.reinforcement_mesh ?? defaults.reinforcementMesh,
                insulationType:
                    match?.insulationType ?? defaults.insulationType,
                tackerFoil: match?.tackerFoil ?? defaults.tackerFoil,
                additionalInsulations:
                    Array.isArray(match?.additionalInsulations) &&
                    match.additionalInsulations.length
                        ? match.additionalInsulations
                        : defaults.additionalInsulations
            };
        },
        /**
         * Returns the numeric floor for an apartment (most frequent floor among its rooms).
         * Used when customization is 2 to colour cards by floor.
         */
        getFloorForApartment(apartmentId) {
            const rooms = this.location?.rooms || [];
            // Match the same way `apartments` computed builds IDs: raw apartament/apartment value as string
            const inApt = rooms.filter((r) => {
                const a = r.apartament ?? r.apartment ?? '0';
                return String(a) === String(apartmentId);
            });
            const floors = inApt
                .map(r => normalizeFloorValue(r.floor))
                .filter(Boolean);
            if (!floors.length) return null;
            const counts = floors.reduce(
                (a, f) => ((a[f] = (a[f] || 0) + 1), a),
                {}
            );
            return (
                Object.keys(counts).sort((a, b) => counts[b] - counts[a])[0]
            );
        },
        /**
         * When customization is 2 (apartments), returns the border colour for the unit card based on floor.
         */
        cardFloorColorForUnit(u) {
            if (this.customizationType !== 2 || u.floorId == null) return null;
            const n = Number(normalizeFloorValue(u.floorId));
            const index = n >= 0 ? n % FLOOR_CARD_COLORS.length : 0;
            return FLOOR_CARD_COLORS[index];
        },
        normalizeUnitId(unitId, type = this.customizationType) {
            if (type !== 2) return unitId;

            if (typeof unitId === 'string' && unitId.startsWith('unit_')) {
                return unitId;
            }

            const normalized = getUnitId(unitId);
            if (normalized) return normalized;

            return unitId == null ? unitId : String(unitId);
        }
    },
    watch: {
        'location.rooms': {
            immediate: true,
            deep: true,
            handler(rooms) {
                if (!rooms?.length) return;

                const currentType = this.customizationType;
                const next = { ...(this.modelValue || {}) };

                this.localRooms = rooms;

                if (!next[currentType]) next[currentType] = {};

                this.customUnits.forEach((u) => {
                    const normalizedId = this.normalizeUnitId(
                        u.id,
                        currentType
                    );
                    const existing = next[currentType][normalizedId];
                    const initial = this.getInitialData(u.id);

                    next[currentType][normalizedId] = {
                        ...initial,
                        ...existing,
                        insulationType:
                            existing?.insulationType || initial.insulationType,
                        tackerFoil: existing?.tackerFoil || initial.tackerFoil,
                        additionalInsulations: existing?.additionalInsulations
                            ?.length
                            ? existing.additionalInsulations
                            : initial.additionalInsulations
                    };
                });

                this.$emit('update:modelValue', next);
            }
        }
    }
};
</script>
